import { CheckIcon } from 'lucide-react'
import React from 'react'

import { StarIcon } from '../icons/Star'
import { ClickToPlayVideo } from '../ui/ClickToPlayVideo'
import { cn } from '../ui/cn'
import { Wrapper } from '../ui/Wrapper'
import { FeedPaginator } from '../ui_patterns/FeedPaginator'

const Grid = Wrapper

interface ReviewCardData {
  description?: string
  author?: string
}

const S3_BASE = 'https://s3.amazonaws.com/shipsticks_partner_assets_production'
const SHIPANDPLAY = `${S3_BASE}/69b1ea22def334404ebf7658`
const HUNTER = `${S3_BASE}/69b1eaa45312dd51426b6cdc`
const TRAVELWITHLIVII = `${S3_BASE}/69b1eaa55312dd51426b6cdd`
const CARAANNEJONES = `${S3_BASE}/69b1eaa65312dd51426b6cde`
const PORTHOLECRUISE = `${S3_BASE}/69b1eaa75312dd51426b6cdf`

const posts: ReviewCardData[] = [
  {
    description:
      'Traveling with a toddler means bringing so much extra gear — Ship&Play made it so' +
      ' much easier.',
    author: 'Janet',
  },
  {
    description:
      'The pickup of my suitcase at the cruise port was flawless… The booking process was' +
      ' easy, and when I called with questions, the operator was friendly and very helpful.',
    author: 'John',
  },
  {
    description:
      'The online booking was easy even for this 68-year-old! It’s cheaper than airline' +
      ' baggage fees and extremely convenient.',
    author: 'Reginald',
  },
  {
    description:
      'Sending 50 lbs of my stuff ahead to my destination — best travel experience I’ve' +
      ' ever had, hands down.',
    author: 'Dina',
  },
  {
    description:
      'Shipment arrived from the USA to Canada in 24 hours — 3 days earlier than expected.' +
      ' Easy to schedule and pay online. ',
    author: 'Jay',
  },
  {
    description:
      'Timeliness was outstanding, and pickup and drop-off couldn’t have been easier... I' +
      ' will absolutely use them again.',
    author: 'Patrick',
  },
]

/**
 * Ship&Play "What Real Users Say" band. Ported verbatim from playground —
 * 4 fixed video review cards + a paginated feed of text reviews. Card
 * content is marketing-invariant so it stays hardcoded here.
 */
export const ReviewFeed = (): React.ReactElement => {
  return (
    <section className="snplay:my-24 snplay:overflow-hidden">
      <div className="snplay-bounds">
        <h1 className="snplay:mb-8 snplay:text-center snplay:text-4xl snplay:font-bold">
          What <span className="snplay:text-primaryDark"> Real Users</span> Say
        </h1>
        <Grid
          className={cn(
            'snplay:mb-20 snplay:grid snplay:grid-cols-1 snplay:gap-4',
            'snplay:xs:grid-cols-2 snplay:lg:grid-cols-4',
          )}
        >
          <VideoReviewCard
            imgUrl={`${SHIPANDPLAY}/images/videoframe_10975.png`}
            videoUrl={`${HUNTER}/videos/video1.mp4`}
            profileUrl={`${SHIPANDPLAY}/images/SnP-avatar.png`}
            author="shipandplayofficial"
          />
          <VideoReviewCard
            imgUrl={`${TRAVELWITHLIVII}/images/video2.png`}
            videoUrl={`${TRAVELWITHLIVII}/videos/copy_09C4152B-9890-43A9-AA8E-988C50C51E93_1_1.mp4`}
            profileUrl={`${TRAVELWITHLIVII}/images/video2.png`}
            author="travelwithlivii"
          />
          <VideoReviewCard
            imgUrl={`${CARAANNEJONES}/images/video3.png`}
            videoUrl={`${CARAANNEJONES}/videos/copy_5A538BC1-C1CB-4E08-8C2B-081048AD4D5A_2.mp4`}
            profileUrl={`${CARAANNEJONES}/images/thumb3.png`}
            author="cara.annejones"
          />
          <VideoReviewCard
            imgUrl={`${PORTHOLECRUISE}/images/video4.png`}
            videoUrl={`${PORTHOLECRUISE}/videos/bill_3.mp4`}
            profileUrl={`${PORTHOLECRUISE}/images/thumb4.png`}
            author="portholecruise"
          />
        </Grid>
        <FeedPaginator prevLabel="Previous reviews" nextLabel="Next reviews">
          {posts.map((post) => (
            <ReviewCard key={post.description} {...post} />
          ))}
        </FeedPaginator>
      </div>
    </section>
  )
}

const ReviewCard = ({ description, author }: ReviewCardData): React.ReactElement => {
  return (
    <article className="snplay:w-80 snplay:shrink-0 snplay:space-y-6 snplay:xs:w-96">
      <div
        className={cn(
          'snplay:mb-6 snplay:flex snplay:items-center',
          'snplay:gap-0.5 snplay:text-primary',
        )}
      >
        <StarIcon className="snplay:size-6" />
        <StarIcon className="snplay:size-6" />
        <StarIcon className="snplay:size-6" />
        <StarIcon className="snplay:size-6" />
        <StarIcon className="snplay:size-6" />
      </div>
      <p className="snplay:text-xl snplay:leading-normal">{description}</p>
      <div className="snplay:text-base snplay:text-ui-secondary">
        &mdash; <span className="snplay:font-semibold">{author}</span>
        <span
          className={cn(
            'snplay:flex snplay:items-center snplay:gap-2',
            'snplay:text-sm snplay:text-ui-secondary',
          )}
        >
          <span
            className={cn(
              'snplay:flex snplay:size-5 snplay:items-center snplay:justify-center',
              'snplay:rounded-full snplay:bg-ui-primary snplay:text-ui-foreground',
            )}
          >
            <CheckIcon className="snplay:block snplay:size-3 snplay:stroke-2" />
          </span>
          Verified Customer
        </span>
      </div>
    </article>
  )
}

interface VideoReviewCardProps {
  imgUrl?: string
  author?: string
  profileUrl?: string
  videoUrl?: string
}

export const VideoReviewCard = ({
  imgUrl,
  author,
  profileUrl,
  videoUrl,
}: VideoReviewCardProps): React.ReactElement => {
  return (
    <ClickToPlayVideo
      posterUrl={imgUrl}
      videoUrl={videoUrl}
      label={author ? `video from ${author}` : 'video'}
      className="snplay:rounded-2xl"
      posterOverlay={
        <div
          className={cn(
            'snplay:pointer-events-none snplay:absolute snplay:right-0 snplay:bottom-0',
            'snplay:left-0 snplay:flex snplay:items-center snplay:gap-2',
            'snplay:bg-gradient-to-b snplay:from-ui-primary/0 snplay:to-ui-primary/60',
            'snplay:p-4 snplay:pt-8',
            'snplay:text-sm snplay:font-bold snplay:text-ui-foreground',
          )}
        >
          <img src={profileUrl} alt={author} className="snplay:size-8 snplay:rounded-full" />
          {author}
        </div>
      }
    />
  )
}
